/**
 * MultiAccountPublishModal Component
 *
 * Modal for publishing campaigns to multiple accounts across platforms.
 * Allows users to select multiple accounts per platform and tracks publishing progress.
 *
 * Features:
 * - Shows all available connections grouped by platform
 * - Multi-select via checkboxes (multiple accounts per platform)
 * - Real-time publishing progress per account
 * - Handles partial failures gracefully
 * - Pre-selects default connections
 * - Shows account health status
 *
 * Usage:
 * ```tsx
 * <MultiAccountPublishModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   conversationId={conversationId}
 *   campaignConfig={config}
 *   platforms={['meta', 'google_ads']}
 *   onPublishComplete={() => handleComplete()}
 * />
 * ```
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../services/api';
import { useToast } from '../ui/Toast';
import { OAuthApiClient } from '../../lib/oauth/OAuthApiClient';
import { PlatformLogo, getPlatformName } from '../icons/PlatformLogos';
import type { PlatformKey } from '../icons/PlatformLogos';
import type { EnhancedOAuthConnection, Platform, ConnectionsByOrgResponse } from '../../lib/oauth/types';
import { classifyPublishError } from '../../utils/publishErrors';

export interface PublishCompletedResult {
  connectionId: string;
  accountName: string;
  platform: Platform;
  status: 'success' | 'failed';
  platformCampaignId?: string;
  finalStatus?: string;
  error?: string;
}

interface MultiAccountPublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  campaignConfig: any; // Full campaign configuration
  platforms: Platform[]; // Platforms included in this campaign
  onPublishComplete?: (results: PublishCompletedResult[]) => void;
}

interface PublishResult {
  connectionId: string;
  accountName: string;
  platform: Platform;
  status: 'pending' | 'publishing' | 'success' | 'failed';
  error?: string;
  platformCampaignId?: string;
}

export const MultiAccountPublishModal: React.FC<MultiAccountPublishModalProps> = ({
  isOpen,
  onClose,
  conversationId,
  campaignConfig,
  platforms,
  onPublishComplete,
}) => {
  const { currentOrganization, user } = useAuth();
  const { toast } = useToast();

  // State
  const [connections, setConnections] = useState<EnhancedOAuthConnection[]>([]);
  const [selectedConnections, setSelectedConnections] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);  // FIX: Start as false, set to true only when actually loading
  const [publishing, setPublishing] = useState(false);
  const [publishResults, setPublishResults] = useState<PublishResult[]>([]);
  const [currentStep, setCurrentStep] = useState<'select' | 'publishing' | 'complete'>('select');
  // Guard to ensure onPublishComplete fires exactly once per publish (polling vs manual close race)
  const publishCompleteCalledRef = useRef(false);

  // Pinterest Boards: { connectionId: { boards, loading } }
  interface PinterestBoardInfo { board_id: string; board_name: string; description: string; privacy: string; pin_count: number; is_default: boolean; }
  const [pinterestBoards, setPinterestBoards] = useState<Record<string, { boards: PinterestBoardInfo[]; loading: boolean }>>({});
  const [selectedBoards, setSelectedBoards] = useState<Record<string, string>>({}); // connectionId -> boardId
  const [newBoardName, setNewBoardName] = useState<Record<string, string>>({});
  const [creatingBoard, setCreatingBoard] = useState<Record<string, boolean>>({});

  // OAuth client
  const oauthClient = new OAuthApiClient();

  const fetchPinterestBoards = useCallback(async (connectionId: string) => {
    let alreadyFetched = false;
    setPinterestBoards(prev => {
      if (prev[connectionId]) { alreadyFetched = true; return prev; }
      return { ...prev, [connectionId]: { boards: [], loading: true } };
    });
    if (alreadyFetched) return;
    try {
      const resp = await apiClient.get(`/api/v1/publisher/pinterest-boards/${connectionId}`);
      const boards = resp.data.boards || [];
      setPinterestBoards(prev => ({ ...prev, [connectionId]: { boards, loading: false } }));
      const defaultBoard = boards.find((b: PinterestBoardInfo) => b.is_default) || boards[0];
      if (defaultBoard) {
        setSelectedBoards(prev => ({ ...prev, [connectionId]: defaultBoard.board_id }));
      }
    } catch {
      setPinterestBoards(prev => ({ ...prev, [connectionId]: { boards: [], loading: false } }));
    }
  }, []);

  const createPinterestBoard = useCallback(async (connectionId: string) => {
    const name = newBoardName[connectionId]?.trim();
    if (!name) return;
    setCreatingBoard(prev => ({ ...prev, [connectionId]: true }));
    try {
      const resp = await apiClient.post('/api/v1/publisher/pinterest-boards', {
        connection_id: connectionId,
        name,
      });
      const board = resp.data;
      setPinterestBoards(prev => {
        const existing = prev[connectionId]?.boards || [];
        return { ...prev, [connectionId]: { boards: [...existing, board], loading: false } };
      });
      setSelectedBoards(prev => ({ ...prev, [connectionId]: board.board_id }));
      setNewBoardName(prev => ({ ...prev, [connectionId]: '' }));
      toast.success(`Board "${name}" created`);
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to create board';
      toast.error(msg);
    } finally {
      setCreatingBoard(prev => ({ ...prev, [connectionId]: false }));
    }
  }, [newBoardName, toast]);

  /**
   * Load connections for current organization and filter by platforms
   * FIXED: Proper error handling with try/catch/finally
   * DEBUG: Comprehensive logging added for troubleshooting
   * NOTE: Wrapped in useCallback to prevent infinite loops in dependency arrays
   */
  const loadConnections = useCallback(async () => {
    console.log('[MultiAccountPublishModal] loadConnections - Starting load', {
      platforms,
    });

    setLoading(true);

    try {
      console.log('[MultiAccountPublishModal] loadConnections - Calling getConnectionsForAllOrganizations');

      // FIX: Use getConnectionsForAllOrganizations instead of getConnectionsEnhanced
      // The backend filters by auth token's organization automatically
      const response = await oauthClient.getConnectionsForAllOrganizations();

      console.log('[MultiAccountPublishModal] loadConnections - All connections received:', {
        total: response.total_connections,
        orgs: response.organizations_count,
        connections_by_org: response.connections_by_org,
        currentOrganizationId: currentOrganization?.id,
      });

      // Filter connections to only include current organization
      let allConnections: EnhancedOAuthConnection[] = [];
      if (currentOrganization?.id && response.connections_by_org[currentOrganization.id]) {
        allConnections = response.connections_by_org[currentOrganization.id];
      } else {
        // Fallback: use first organization if current org not found
        const firstOrgConnections = Object.values(response.connections_by_org)[0];
        if (firstOrgConnections) {
          allConnections = firstOrgConnections;
        }
      }

      console.log('[MultiAccountPublishModal] loadConnections - Filtered to current org:', {
        organizationId: currentOrganization?.id,
        connectionCount: allConnections.length,
      });

      console.log('[MultiAccountPublishModal] loadConnections - Flattened connections:', {
        count: allConnections.length,
        connections: allConnections.map((c) => ({
          id: c.id,
          organization_id: c.organization_id,
          platform: c.platform,
          is_default: c.is_default,
          status: c.status,
          account_name: c.account_name,
          revoked_at: c.revoked_at,
        })),
      });

      // Filter for relevant platforms only
      console.log('[MultiAccountPublishModal] loadConnections - Filtering by platforms:', {
        expectedPlatforms: platforms,
        hasPlatforms: platforms && platforms.length > 0,
      });

      const relevantConnections = allConnections.filter((conn) => {
        // If no platforms specified, include all connections (show all available accounts)
        const platformMatch = !platforms || platforms.length === 0 || platforms.includes(conn.platform);
        const notRevoked = !conn.revoked_at;

        const passes = platformMatch && notRevoked;

        if (!passes) {
          console.log('[MultiAccountPublishModal] loadConnections - Connection filtered out:', {
            id: conn.id,
            platformMatch,
            notRevoked,
            actual: {
              platform: conn.platform,
              revoked_at: conn.revoked_at,
            },
          });
        }

        return passes;
      });

      console.log('[MultiAccountPublishModal] loadConnections - After filtering:', {
        relevantCount: relevantConnections.length,
        relevant: relevantConnections.map((c) => ({
          id: c.id,
          organization_id: c.organization_id,
          platform: c.platform,
          account_name: c.account_name,
          is_default: c.is_default,
          status: c.status,
        })),
      });

      setConnections(relevantConnections);

      // Auto-select default connections
      const defaultConnectionIds = relevantConnections
        .filter((conn) => conn.is_default && conn.status === 'valid')
        .map((conn) => conn.id);

      console.log('[MultiAccountPublishModal] loadConnections - Auto-selected defaults:', {
        count: defaultConnectionIds.length,
        ids: defaultConnectionIds,
      });

      setSelectedConnections(new Set(defaultConnectionIds));

      // Fetch boards for auto-selected Pinterest connections
      for (const conn of relevantConnections) {
        if (conn.is_default && conn.status === 'valid' && conn.platform === 'pinterest') {
          fetchPinterestBoards(conn.id);
        }
      }
    } catch (error) {
      console.error('[MultiAccountPublishModal] loadConnections - Exception caught:', error);
      // Clear connections on error to show empty state
      setConnections([]);
    } finally {
      // CRITICAL FIX: Always reset loading state, even on error
      console.log('[MultiAccountPublishModal] loadConnections - Setting loading to false');
      setLoading(false);
    }
  }, [platforms, currentOrganization]); // useCallback dependencies - added currentOrganization

  /**
   * Load connections when modal opens or organization changes
   * FIX: Now depends on currentOrganization to reload when org is switched
   */
  useEffect(() => {
    console.log('[MultiAccountPublishModal] useEffect triggered:', {
      isOpen,
      currentOrgId: currentOrganization?.id,
    });

    if (isOpen) {
      console.log('[MultiAccountPublishModal] useEffect - Modal opened, calling loadConnections');
      loadConnections();
    } else {
      // Clear connections when modal closes to reset state
      console.log('[MultiAccountPublishModal] useEffect - Modal closed, clearing state');
      setConnections([]);
      setLoading(false);
      setPinterestBoards({});
      setSelectedBoards({});
      setNewBoardName({});
      setCreatingBoard({});
    }
  }, [isOpen, loadConnections, currentOrganization?.id]); // Reload when org changes

  /**
   * Listen for organization changes to reload connections
   * FIXED: Modal now listens to organizationChanged event and triggers refresh
   */
  useEffect(() => {
    const handleOrgChanged = () => {
      if (isOpen && currentOrganization) {
        loadConnections();
      }
    };

    window.addEventListener('organizationChanged', handleOrgChanged);
    return () => window.removeEventListener('organizationChanged', handleOrgChanged);
  }, [isOpen, currentOrganization, loadConnections]);

  /**
   * Toggle connection selection
   */
  const toggleConnection = (connectionId: string) => {
    const conn = connections.find(c => c.id === connectionId);
    setSelectedConnections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(connectionId)) {
        newSet.delete(connectionId);
      } else {
        newSet.add(connectionId);
        if (conn?.platform === 'pinterest') {
          fetchPinterestBoards(connectionId);
        }
      }
      return newSet;
    });
  };

  /**
   * Get connections grouped by platform
   */
  const getConnectionsByPlatform = () => {
    const grouped: Record<Platform, EnhancedOAuthConnection[]> = {
      meta: [],
      google_ads: [],
      tiktok: [],
      pinterest: [],
      twitter: [],
    };

    connections.forEach((conn) => {
      if (grouped[conn.platform]) {
        grouped[conn.platform].push(conn);
      }
    });

    return grouped;
  };

  /**
   * Poll platform status for successfully published campaigns.
   * Tries up to 6 times with 10s intervals per result.
   * Updates the results list in-place, then calls onPublishComplete with final data.
   */
  const pollCampaignStatuses = useCallback(
    async (topoll: PublishResult[], allResults: PublishResult[]) => {
      const MAX_TRIES = 6;
      const INTERVAL_MS = 10_000;

      const finalResults = [...allResults];

      await Promise.all(
        topoll.map(async (result) => {
          for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
            if (attempt > 0) {
              await new Promise((res) => setTimeout(res, INTERVAL_MS));
            }
            try {
              const resp = await apiClient.get(
                `/api/v1/publisher/status/${result.platform}/${result.platformCampaignId}`
              );
              const platformStatus: string = resp.data?.status || resp.data?.campaign_status || '';
              if (platformStatus) {
                const idx = finalResults.findIndex((r) => r.connectionId === result.connectionId);
                if (idx !== -1) {
                  finalResults[idx] = { ...finalResults[idx], finalStatus: platformStatus } as any;
                  setPublishResults([...finalResults]);
                }
                // Stop polling once we have a definitive status
                if (['ACTIVE', 'PAUSED', 'ENABLED', 'DISAPPROVED', 'ERROR'].includes(platformStatus.toUpperCase())) {
                  break;
                }
              }
            } catch {
              // Ignore poll errors — best-effort
            }
          }
        })
      );

      if (onPublishComplete && !publishCompleteCalledRef.current) {
        publishCompleteCalledRef.current = true;
        onPublishComplete(
          finalResults.map((r) => ({
            connectionId: r.connectionId,
            accountName: r.accountName,
            platform: r.platform,
            status: r.status as 'success' | 'failed',
            platformCampaignId: r.platformCampaignId,
            finalStatus: (r as any).finalStatus,
            error: r.error,
          }))
        );
      }
    },
    [onPublishComplete]
  );

  /**
   * Handle publish to selected accounts
   */
  const handlePublish = async () => {
    if (selectedConnections.size === 0) {
      toast.warning('Please select at least one account to publish to');
      return;
    }

    if (!user) {
      toast.error('User not authenticated');
      return;
    }

    try {
      setPublishing(true);
      setCurrentStep('publishing');
      publishCompleteCalledRef.current = false;

      // Initialize publish results
      const initialResults: PublishResult[] = Array.from(selectedConnections).map((connId: string) => {
        const conn = connections.find((c) => c.id === connId)!;
        return {
          connectionId: connId,
          accountName: conn.account_name || 'Unnamed Account',
          platform: conn.platform,
          status: 'pending' as const,
        };
      });
      setPublishResults(initialResults);

      // Group selected connections by platform with account IDs
      const selectedByPlatform: Record<string, Array<{id: string, account_id: string, board_id?: string}>> = {};
      selectedConnections.forEach((connId) => {
        const conn = connections.find((c) => c.id === connId);
        if (conn) {
          if (!selectedByPlatform[conn.platform]) {
            selectedByPlatform[conn.platform] = [];
          }
          // Use platform_account_id if available, fallback to platform_user_id for compatibility
          const accountId = conn.platform_account_id || conn.platform_user_id || 'default_account';
          const item: {id: string, account_id: string, board_id?: string} = {
            id: connId,
            account_id: accountId,
          };
          // Include board_id for Pinterest connections
          if (conn.platform === 'pinterest' && selectedBoards[connId]) {
            item.board_id = selectedBoards[connId];
          }
          selectedByPlatform[conn.platform].push(item);
        }
      });

      console.log('[MultiAccountPublishModal] Grouped connections by platform:', {
        platforms: Object.keys(selectedByPlatform),
        selectedByPlatform: Object.entries(selectedByPlatform).map(([p, conns]) => ({
          platform: p,
          count: conns.length,
          accounts: conns.map(c => c.account_id)
        }))
      });

      // Call the new multi-account publish endpoint with updated request format
      // Extract campaign title from config or use a default
      const campaignTitle = campaignConfig?.title || 'Campaign ' + conversationId;

      const response = await apiClient.post('/api/v1/publisher/publish-multi', {
        conversation_id: conversationId,
        campaign_title: campaignTitle,
        campaign_config: campaignConfig,
        selected_connections: selectedByPlatform,
        user_id: user.id,
      });

      console.log('[MultiAccountPublishModal] Publish response received:', {
        total_accounts: response.data.total_accounts,
        successful: response.data.successful,
        failed: response.data.failed,
        results_count: response.data.results?.length || 0,
      });

      // Update results based on response - fix status field mapping
      const results: PublishResult[] = response.data.results.map((result: any) => {
        // Prefer the account name from our local connections state — backend may return 'Unknown'
        const localConn = connections.find((c) => c.id === result.connection_id);
        return {
          connectionId: result.connection_id,
          accountName: (result.account_name && result.account_name !== 'Unknown')
            ? result.account_name
            : (localConn?.account_name || 'Unnamed Account'),
          platform: result.platform,
          status: result.status || (result.success ? 'success' : 'failed'),
          error: result.error,
          platformCampaignId: result.platform_campaign_id,
        };
      });

      console.log('[MultiAccountPublishModal] Mapped results:', {
        total: results.length,
        successful: results.filter(r => r.status === 'success').length,
        failed: results.filter(r => r.status === 'failed').length,
      });

      setPublishResults(results);
      setCurrentStep('complete');

      // Poll platform status for successfully published campaigns
      const successfulResults = results.filter((r) => r.status === 'success' && r.platformCampaignId);
      if (successfulResults.length > 0) {
        pollCampaignStatuses(successfulResults, results);
      } else {
        // No successful results — notify immediately
        if (onPublishComplete) {
          onPublishComplete(
            results.map((r) => ({
              connectionId: r.connectionId,
              accountName: r.accountName,
              platform: r.platform,
              status: r.status as 'success' | 'failed',
              platformCampaignId: r.platformCampaignId,
              error: r.error,
            }))
          );
        }
      }
    } catch (error: any) {
      console.error('Failed to publish:', error);
      const errMsg = error.response?.data?.detail || error.message || 'Unknown error';

      // Mark all as failed and move to complete step to show structured errors
      setPublishResults((prev) =>
        prev.map((r) => ({
          ...r,
          status: 'failed',
          error: errMsg,
        }))
      );
      setCurrentStep('complete');
    } finally {
      setPublishing(false);
    }
  };

  /**
   * Get platform icon using shared module
   */
  const getPlatformIcon = (platform: Platform) => (
    <PlatformLogo platform={platform as PlatformKey} size="sm" />
  );

  /**
   * Close and reset
   */
  const handleClose = () => {
    if (publishing) return; // Don't allow close while publishing

    // If closing from the complete step, fire onPublishComplete with current results
    // so ChatInterface can inject the status message regardless of polling state.
    if (currentStep === 'complete' && publishResults.length > 0 && onPublishComplete && !publishCompleteCalledRef.current) {
      publishCompleteCalledRef.current = true;
      onPublishComplete(
        publishResults.map((r) => ({
          connectionId: r.connectionId,
          accountName: r.accountName,
          platform: r.platform,
          status: r.status as 'success' | 'failed',
          platformCampaignId: r.platformCampaignId,
          finalStatus: (r as any).finalStatus,
          error: r.error,
        }))
      );
    }

    setCurrentStep('select');
    setPublishResults([]);
    onClose();
  };

  // Don't render if not open
  if (!isOpen) return null;

  const connectionsByPlatform = getConnectionsByPlatform();
  const selectedCount = selectedConnections.size;

  // Debug logging for rendering
  console.log('[MultiAccountPublishModal] Rendering:', {
    connections: connections.length,
    connectionsByPlatform: Object.entries(connectionsByPlatform).map(([p, conns]) => ({
      platform: p,
      count: conns.length,
    })),
    selectedCount,
    loading,
  });

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] animate-fadeIn" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-[1001] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="relative w-full max-w-4xl bg-card dark:bg-card-dark rounded-2xl shadow-2xl border border-line dark:border-border-dark animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-line dark:border-border-dark">
              <div>
                <h2 className="text-lg font-semibold text-ink">
                  {currentStep === 'select' && 'Select Accounts to Publish'}
                  {currentStep === 'publishing' && 'Publishing Campaign...'}
                  {currentStep === 'complete' && 'Publishing Complete'}
                </h2>
                <p className="text-sm text-ink-2 mt-1">
                  {currentStep === 'select' &&
                    'Select one or more accounts per platform to publish your campaign'}
                  {currentStep === 'publishing' && 'Please wait while we publish to selected accounts'}
                  {currentStep === 'complete' && 'Campaign publishing results'}
                </p>
              </div>
              {!publishing && (
                <button
                  onClick={handleClose}
                  className="p-2 text-ink-3 hover:text-ink-2 rounded-lg hover:bg-inset transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              {/* Step 1: Account Selection */}
              {currentStep === 'select' && (
                <>
                  {loading ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <span className="ml-3 text-ink-2">
                        Loading accounts...
                      </span>
                    </div>
                  ) : connections.length === 0 ? (
                    <div className="text-center py-12 bg-ground rounded-lg border-2 border-dashed border-line-2 dark:border-border-dark">
                      <svg
                        className="mx-auto w-16 h-16 text-ink-3 mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                      </svg>
                      <h3 className="text-lg font-medium text-ink mb-2">
                        No Connected Accounts
                      </h3>
                      <p className="text-sm text-ink-2 mb-4">
                        You need to connect at least one account before publishing
                      </p>
                      <button
                        onClick={() => {
                          handleClose();
                          window.location.href = '/settings/businesses/active/connections';
                        }}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        Connect Accounts
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {(() => {
                        const entries = Object.entries(connectionsByPlatform);
                        console.log('[MultiAccountPublishModal] Rendering accounts list:', {
                          totalEntries: entries.length,
                          entries: entries.map(([p, conns]) => ({
                            platform: p,
                            count: conns.length,
                          })),
                        });
                        return entries;
                      })().map(([platform, platformConnections]) => {
                        // Skip platforms with no connections
                        if (platformConnections.length === 0) {
                          console.log('[MultiAccountPublishModal] Skipping platform with no connections:', platform);
                          return null;
                        }

                        console.log('[MultiAccountPublishModal] Rendering platform section:', {
                          platform,
                          connectionCount: platformConnections.length,
                        });

                        const platformKey = platform as Platform;

                        return (
                          <div key={platform}>
                            <div className="flex items-center gap-3 mb-3">
                              {getPlatformIcon(platformKey)}
                              <h3 className="font-semibold text-ink">
                                {getPlatformName(platformKey)}
                              </h3>
                              <span className="text-xs text-ink-3">
                                ({platformConnections.length} account
                                {platformConnections.length !== 1 ? 's' : ''})
                              </span>
                            </div>

                            <div className="space-y-2 ml-11">
                              {platformConnections.map((connection) => {
                                const isSelected = selectedConnections.has(connection.id);
                                const isPinterest = connection.platform === 'pinterest';
                                const connBoards = isPinterest ? pinterestBoards[connection.id] : null;
                                const boardsFetched = isPinterest && isSelected && connBoards && !connBoards.loading;
                                const boardsLoaded = boardsFetched && connBoards!.boards.length > 0;
                                const showBoardPicker = boardsLoaded && connBoards!.boards.length > 1;
                                const showBoardConfirm = boardsLoaded && connBoards!.boards.length === 1;
                                const showBoardCreate = isPinterest && isSelected && boardsFetched && connBoards!.boards.length === 0;
                                const hasBoardSubSection = showBoardPicker || showBoardConfirm || showBoardCreate || (isPinterest && isSelected && connBoards?.loading);

                                return (
                                <div key={connection.id}>
                                <label
                                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                    isSelected
                                      ? 'bg-work-soft border-work/40'
                                      : 'bg-ground border-line dark:border-border-dark hover:border-line-2'
                                  } ${hasBoardSubSection ? 'rounded-b-none border-b-0' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleConnection(connection.id)}
                                    className="w-5 h-5 text-primary border-line-2 rounded focus:ring-2 focus:ring-primary"
                                  />

                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-ink">
                                        {connection.account_name || 'Unnamed Account'}
                                      </span>
                                      {connection.is_default && (
                                        <span className="px-2 py-0.5 bg-work-soft text-work text-xs font-medium rounded">
                                          Default
                                        </span>
                                      )}
                                      {connection.status === 'valid' && (
                                        <span className="flex items-center gap-1 text-xs text-ok">
                                          <div className="w-1.5 h-1.5 bg-ok rounded-full"></div>
                                          Active
                                        </span>
                                      )}
                                      {connection.requires_reauth && (
                                        <span className="flex items-center gap-1 text-xs text-orange-600">
                                          <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                                          Reauth Required
                                        </span>
                                      )}
                                    </div>
                                    {connection.connection_label && (
                                      <p className="text-xs text-ink-2 mt-0.5">
                                        {connection.connection_label}
                                      </p>
                                    )}
                                  </div>
                                </label>
                                {/* Pinterest board loading */}
                                {isPinterest && isSelected && connBoards?.loading && (
                                  <div className="px-4 py-2 bg-bad-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                    <div className="flex items-center gap-2 text-xs text-ink-3">
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                                      Loading boards...
                                    </div>
                                  </div>
                                )}
                                {/* Single board confirmation */}
                                {showBoardConfirm && (
                                  <div className="px-4 py-2.5 bg-bad-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                    <div className="flex items-center gap-2 text-xs text-ink-2">
                                      <svg className="w-3.5 h-3.5 text-bad" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
                                      </svg>
                                      Pinning to <span className="font-medium text-ink">{connBoards!.boards[0].board_name}</span>
                                    </div>
                                  </div>
                                )}
                                {/* Board picker for multiple boards */}
                                {showBoardPicker && (
                                  <div className="px-4 py-3 bg-bad-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                    <p className="text-xs font-medium text-ink-2 mb-2">
                                      Pin to Board:
                                    </p>
                                    <div className="space-y-1.5">
                                      {connBoards!.boards.map((board) => (
                                        <label
                                          key={board.board_id}
                                          className={`flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                                            selectedBoards[connection.id] === board.board_id
                                              ? 'bg-card shadow-sm'
                                              : 'hover:bg-card/60'
                                          }`}
                                        >
                                          <input
                                            type="radio"
                                            name={`board-${connection.id}`}
                                            checked={selectedBoards[connection.id] === board.board_id}
                                            onChange={() => setSelectedBoards(prev => ({ ...prev, [connection.id]: board.board_id }))}
                                            className="w-4 h-4 text-primary border-line-2 focus:ring-2 focus:ring-primary"
                                          />
                                          <div className="flex-1 min-w-0">
                                            <span className="text-sm font-medium text-ink">
                                              {board.board_name}
                                            </span>
                                            <span className="ml-2 text-xs text-ink-3">
                                              {board.pin_count} pin{board.pin_count !== 1 ? 's' : ''}
                                            </span>
                                          </div>
                                          {board.is_default && (
                                            <span className="px-1.5 py-0.5 bg-bad-soft text-bad text-[10px] font-medium rounded">
                                              Default
                                            </span>
                                          )}
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* Create board for Pinterest - shown when no boards exist */}
                                {showBoardCreate && (
                                  <div className="px-4 py-3 bg-bad-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                    <p className="text-xs text-ink-2 mb-2">
                                      No boards found. Create one to start pinning:
                                    </p>
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        placeholder="Board name"
                                        value={newBoardName[connection.id] || ''}
                                        onChange={(e) => setNewBoardName(prev => ({ ...prev, [connection.id]: e.target.value }))}
                                        onKeyDown={(e) => { if (e.key === 'Enter') createPinterestBoard(connection.id); }}
                                        className="flex-1 px-3 py-1.5 text-sm border border-line-2 rounded-md bg-card text-ink focus:ring-2 focus:ring-primary focus:border-primary"
                                        disabled={creatingBoard[connection.id]}
                                      />
                                      <button
                                        onClick={() => createPinterestBoard(connection.id)}
                                        disabled={!newBoardName[connection.id]?.trim() || creatingBoard[connection.id]}
                                        className="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                      >
                                        {creatingBoard[connection.id] ? 'Creating...' : 'Create Board'}
                                      </button>
                                    </div>
                                  </div>
                                )}
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Step 2: Publishing Progress */}
              {currentStep === 'publishing' && (
                <div className="space-y-3">
                  {publishResults.map((result) => (
                    <div
                      key={result.connectionId}
                      className="flex items-center gap-3 p-4 bg-ground rounded-lg border border-line dark:border-border-dark"
                    >
                      {getPlatformIcon(result.platform)}

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-ink">
                          {result.accountName}
                        </p>
                        <p className="text-xs text-ink-2">
                          {getPlatformName(result.platform)}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {result.status === 'pending' && (
                          <span className="text-sm text-ink-3">Waiting...</span>
                        )}
                        {result.status === 'publishing' && (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                            <span className="text-sm text-primary">Publishing...</span>
                          </>
                        )}
                        {result.status === 'success' && (
                          <span className="flex items-center gap-1 text-sm text-ok">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Success
                          </span>
                        )}
                        {result.status === 'failed' && (
                          <span className="flex items-center gap-1 text-sm text-bad">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Failed
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Step 3: Results Summary */}
              {currentStep === 'complete' && (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="p-4 bg-work-soft rounded-lg border border-work/30">
                      <p className="text-2xl font-bold text-blue-900">
                        {publishResults.length}
                      </p>
                      <p className="text-sm text-primary">Total Accounts</p>
                    </div>
                    <div className="p-4 bg-ok-soft rounded-lg border border-ok/30">
                      <p className="text-2xl font-bold text-green-900">
                        {publishResults.filter((r) => r.status === 'success').length}
                      </p>
                      <p className="text-sm text-ok">Successful</p>
                    </div>
                    <div className="p-4 bg-bad-soft rounded-lg border border-bad/30">
                      <p className="text-2xl font-bold text-red-900">
                        {publishResults.filter((r) => r.status === 'failed').length}
                      </p>
                      <p className="text-sm text-bad">Failed</p>
                    </div>
                  </div>

                  {/* Detailed Results */}
                  <div className="space-y-3">
                    {publishResults.map((result) => (
                      <div
                        key={result.connectionId}
                        className={`p-4 rounded-lg border-2 ${
                          result.status === 'success'
                            ? 'bg-ok-soft border-ok/30'
                            : 'bg-bad-soft border-bad/30'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {getPlatformIcon(result.platform)}

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-ink">
                                {result.accountName}
                              </p>
                              {result.status === 'success' && (
                                <svg
                                  className="w-5 h-5 text-ok"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                              {result.status === 'failed' && (
                                <svg
                                  className="w-5 h-5 text-bad"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <p className="text-xs text-ink-2">
                              {getPlatformName(result.platform)}
                            </p>
                            {result.platformCampaignId && (
                              <p className="text-xs text-ink-3 mt-1">
                                Campaign ID: {result.platformCampaignId}
                              </p>
                            )}
                            {result.error && (() => {
                              const errInfo = classifyPublishError(result.error);
                              return (
                                <div className="mt-2 p-2 rounded-md bg-bad-soft border border-bad/30">
                                  <p className="text-xs font-semibold text-bad">
                                    {errInfo.title}
                                  </p>
                                  <p className="text-xs text-bad mt-0.5">
                                    {errInfo.hint}
                                  </p>
                                  {errInfo.action && errInfo.actionRoute && (
                                    <button
                                      onClick={() => { window.location.href = errInfo.actionRoute!; }}
                                      className="mt-1.5 text-xs font-medium text-bad underline hover:no-underline"
                                    >
                                      {errInfo.action} →
                                    </button>
                                  )}
                                  <details className="mt-1.5">
                                    <summary className="text-[10px] text-bad cursor-pointer select-none">
                                      Raw error
                                    </summary>
                                    <p className="text-[10px] text-bad mt-0.5 font-mono break-all">
                                      {result.error}
                                    </p>
                                  </details>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-line dark:border-border-dark rounded-b-2xl bg-ground">
              {currentStep === 'select' && (
                <>
                  <p className="text-sm text-ink-2">
                    {selectedCount} account{selectedCount !== 1 ? 's' : ''} selected
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleClose}
                      className="px-6 py-2 bg-inset text-ink-2 font-medium rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePublish}
                      disabled={selectedCount === 0 || loading}
                      className="px-6 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Publish to {selectedCount} Account{selectedCount !== 1 ? 's' : ''}
                    </button>
                  </div>
                </>
              )}

              {currentStep === 'publishing' && (
                <div className="flex-1 text-center">
                  <p className="text-sm text-ink-2">
                    Publishing in progress... Please do not close this window
                  </p>
                </div>
              )}

              {currentStep === 'complete' && (
                <>
                  <div className="text-sm text-ink-2">
                    {publishResults.filter((r) => r.status === 'success').length === publishResults.length ? (
                      <span className="text-ok font-medium">
                        ✓ All campaigns published successfully
                      </span>
                    ) : (
                      <span className="text-orange-600 font-medium">
                        ⚠ Some campaigns failed to publish
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleClose}
                    className="px-6 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MultiAccountPublishModal;
